import { computed, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import {
  isPaymentProjectionComplete,
  portalErrorMessage,
  type ExpertSubscriptionOverview,
} from "./expertSubscription";

interface ExpertBillingStatusApi {
  getSubscriptionOverview: () => Promise<ExpertSubscriptionOverview>;
  createPortalSession: () => Promise<{ url: string }>;
}

const confirmationPollMs = 1500;
const confirmationTimeoutMs = 60_000;

export function shouldRefetchForConfirmationTransition(options: {
  confirming: boolean;
  wasConfirming: boolean | undefined;
  enabled: boolean;
  overview: ExpertSubscriptionOverview | null | undefined;
}) {
  return options.confirming
    && options.wasConfirming === false
    && options.enabled
    && !isPaymentProjectionComplete(options.overview);
}

export function useExpertSubscriptionOverview(options: {
  enabled: MaybeRefOrGetter<boolean>;
  confirmingPayment: MaybeRefOrGetter<boolean>;
}) {
  const api = useApi();
  // The ignored legacy-api dist declarations can lag workspace source during
  // a stacked PR. Runtime resolves the schema-validated source client.
  const billingApi = (api.expert as typeof api.expert & {
    billing: ExpertBillingStatusApi;
  }).billing;
  const confirmationTimedOut = ref(false);
  const projectionComplete = ref(false);
  const portalError = ref<string | null>(null);
  let confirmationTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  function clearConfirmationTimeout() {
    if (confirmationTimeoutTimer) clearTimeout(confirmationTimeoutTimer);
    confirmationTimeoutTimer = null;
  }

  watch(
    () => toValue(options.confirmingPayment),
    (confirming) => {
      clearConfirmationTimeout();
      confirmationTimedOut.value = false;
      if (confirming) {
        confirmationTimeoutTimer = setTimeout(() => {
          confirmationTimedOut.value = true;
          confirmationTimeoutTimer = null;
        }, confirmationTimeoutMs);
      }
    },
    { immediate: true },
  );

  const confirmationPollingInterval = computed(() => (
    toValue(options.confirmingPayment)
      && !confirmationTimedOut.value
      && !projectionComplete.value
      ? confirmationPollMs
      : false
  ));

  const query = useQuery({
    queryKey: ["expert", "billing", "subscription-overview"],
    enabled: computed(() => toValue(options.enabled)),
    queryFn: () => billingApi.getSubscriptionOverview(),
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: confirmationPollingInterval,
  });

  watch(
    query.data,
    (overview) => {
      projectionComplete.value = isPaymentProjectionComplete(overview);
    },
    { immediate: true },
  );

  const isPaymentConfirmed = computed(() => projectionComplete.value);

  watch(
    () => toValue(options.confirmingPayment),
    (confirming, wasConfirming) => {
      if (!shouldRefetchForConfirmationTransition({
        confirming,
        wasConfirming,
        enabled: toValue(options.enabled),
        overview: query.data.value,
      })) return;
      void query.refetch();
    },
  );

  watch(isPaymentConfirmed, (confirmed) => {
    if (!confirmed) return;
    clearConfirmationTimeout();
    confirmationTimedOut.value = false;
  }, { immediate: true });

  onScopeDispose(clearConfirmationTimeout);

  const portalMutation = useMutation({
    mutationFn: () => billingApi.createPortalSession(),
    onMutate: () => {
      portalError.value = null;
    },
    onError: (error) => {
      portalError.value = portalErrorMessage(error);
    },
  });

  async function openBillingPortal() {
    try {
      const session = await portalMutation.mutateAsync();
      window.location.assign(session.url);
    } catch {
      // The stable error is rendered inline by the mutation handler.
    }
  }

  return {
    overview: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    isPaymentConfirmed,
    confirmationTimedOut,
    openBillingPortal,
    isOpeningPortal: portalMutation.isPending,
    portalError,
  };
}
